import { derived } from 'overmind';

import { createGenericFetchMachine } from '@/common/overmind/machines/generic-fetch-machine';
import type { GenericFetchMachineType } from '@/common/overmind/machines/generic-fetch-machine';
import type { UnionToIntersection } from '@/common/utils/UnionToIntersection';
import type { CalculationSets } from '@/overmind/effects/api/orchestratorApi';

import type {
  AvailableCalculationScenarios,
  CalculationTactic,
  CompletedCalculationTactic,
  DomainGuid,
  GridElementSectionWithQuickAccessResult,
  RawData,
  SelectedGridElement
} from '.';
import { isCalculationTacticResultUnavailable } from './actions';
import type { CalculationContext, TacticResultBrowsingFilter } from './actions';
import { createDefaultEvaluationRules } from './createDefaultEvaluationRules';
import { createGridElementCalculationSetExecutionDetails } from './createGridElementCalculationSetExecutionDetails';
import { availableCalculationScenarios } from './derived-state/availableCalculationScenarios';
import type { CalculationSetStatistics } from './derived-state/calculationSetStatistics/CalculationSetStatistics.types';
import { calculationSetStatistics } from './derived-state/calculationSetStatistics/calculationSetStatistics';
import { calculationTacticExecutionStatusText } from './derived-state/calculationTacticExecutionStatusText';
import { evaluatedGridElements } from './derived-state/evaluatedGridElements';
import type { GridElementType } from './derived-state/evaluatedGridElements/GridElement';
import { functionalGridElements } from './derived-state/functionalGridElements';
import { gridElementsAttentionSummary } from './derived-state/gridElementsAttentionSummary';
import type { EvaluatedGridElementType } from './derived-state/gridElementsDetailsByCalculationType/GridElementType';
import type {
  GridElementsCalculationSetExecutionResults,
  GridElementsDetailsByCalculationType
} from './derived-state/gridElementsDetailsByCalculationType/GridElementsDetails';
import { createDetailsFilteredBySelectedScenarios } from './derived-state/gridElementsDetailsByCalculationType/createDetailsFilteredBySelectedScenarios';
import { loadFlowSummary } from './derived-state/loadFlowSummary';
import type { LoadFlowSummary } from './derived-state/loadFlowSummary';
import { getLoadFlowScenarioSummary } from './derived-state/loadFlowSummary/summary';
import { selectedGridElement } from './derived-state/selectedGridElement';
import { shortCircuitSummary } from './derived-state/shortCircuitSummary';
import type { ShortCircuitSummary } from './derived-state/shortCircuitSummary';
import type { ScenarioIdType } from './domain/ScenarioId';
import type { GridElementPropertyAttentionStatusByCalculationType } from './evaluation/AttentionStatusRepository';
import type { EvaluationRule, EvaluationRules } from './evaluation/EvaluationRule';
import { countShortCircuitIssuesRequiringAttention } from './evaluation/shortCircuit/attentionRequired';
import type {
  FunctionalGrid,
  FunctionalGridElementTypes
} from './grid/FunctionalGrid';
import type { CalculationTacticResultsBrowsingMachine } from './machines/calculationTacticResultsBrowsingMachine';
import { createCalculationTacticResultsBrowsingMachine } from './machines/calculationTacticResultsBrowsingMachine';
import { externallyTriggeredCalculationSetExecution } from './machines/externallyTriggeredCalculationSetExecution';
import { initiatingGridElement } from './initiatingGridElement';

type CalculationContextX =
  | { type: 'NOT_AVAILABLE' }
  | { type: 'AVAILABLE'; calculationContext: CalculationContext };

export type CalculationState = {
  initiatingGridElement: { globalId: DomainGuid, type: "NetworkAssembly" | "Other" } | undefined
  calculationContext: CalculationContextX;
  gridVersionName: string | undefined;
  externallyTriggeredCalculationSetExecution: ReturnType<
    typeof externallyTriggeredCalculationSetExecution.create
  >;
  calculationSets: GenericFetchMachineType<CalculationSets>;

  functionalGridElements:
    | { type: 'NOT_AVAILABLE' }
    | {
        type: 'FUNCTIONAL_GRID_ELEMENTS';
        gridElements: (UnionToIntersection<
          {
            [TGridElementType in keyof FunctionalGrid]: FunctionalGrid[TGridElementType];
          }[keyof FunctionalGrid]
        >[number] & { objectType: FunctionalGridElementTypes })[];
      };
  calculationSetStatistics:
    | { type: 'NO_STATISTICS' }
    | {
        type: 'CALCULATION_SET_STATISTICS';
        statistics: CalculationSetStatistics;
      };
  isCalculationTacticResultUnavailable: boolean;
  calculationTacticNotification:
    | { type: 'NO_NOTIFICATION' }
    | { type: 'FAILURE_NOTIFICATION'; notificationText: string }
    | { type: 'SUCCESSFUL_NOTIFICATION'; notificationText: string };
  calculationTacticExecutionStatusText:
    | { type: 'NO_TEXT' }
    | { type: 'LOADING_TEXT'; text: string };
  availableCalculationScenarios: AvailableCalculationScenarios;
  calculationTactic: GenericFetchMachineType<CalculationTactic>;
  evaluationRules: EvaluationRules;
  tacticResultBrowsingFilter: TacticResultBrowsingFilter;
  previousTacticResultBrowsingFilter: TacticResultBrowsingFilter; // TODO: this is not the best way for achieving preservation of previously selected filter
  // entire filtering process needs a redesign
  loadFlowSummary:
    | { type: 'NO_SUMMARY_AVAILABLE' }
    | { type: 'SUMMARY_AVAILABLE'; summary: LoadFlowSummary };
  loadFlowScenariosRequiringAttention:
    | { type: 'NOT_AVAILABLE' }
    | { type: 'AVAILABLE'; scenarios: Record<ScenarioIdType, boolean> };
  shortCircuitSummary:
    | { type: 'NO_SUMMARY_AVAILABLE' }
    | { type: 'SUMMARY_AVAILABLE'; summary: ShortCircuitSummary };
  shortCircuitCalculationRequiresAttention:
    | { type: 'NOT_AVAILABLE' }
    | { type: 'AVAILABLE'; requiresAttention: boolean };
  gridElementsDetailsByCalculationType:
    | { type: 'NO_EVALUATION' }
    | {
        type: 'EVALUATED';
        gridElementsDetails: GridElementsDetailsByCalculationType;
        /* this is temporary property - move it to separate state and use in this derive */ attentionStatuses: GridElementPropertyAttentionStatusByCalculationType;
      };
  gridElementCalculationSetExecutionDetails:
    | { type: 'NO_EVALUATION' }
    | {
        type: 'EVALUATED';
        details: GridElementsCalculationSetExecutionResults;
      };
  gridElementsAttentionStatuses:
    | { type: 'NOT_AVAILABLE' }
    | {
        type: 'AVAILABLE';
        byCalculationType: GridElementPropertyAttentionStatusByCalculationType;
      };
  gridElementDetails:
    | { type: 'NO_EVALUATION' }
    | { type: 'EVALUATED'; elements: GridElementType[] };
  gridElementsAttentionSummary:
    | { type: 'NO_GRID_ELEMENTS_SUMMARY' }
    | {
        type: 'GRID_ELEMENTS_SUMMARY_AVAILABLE';
        summary: GridElementSectionWithQuickAccessResult[];
      };
  resultsBrowsingMode: CalculationTacticResultsBrowsingMachine;
  selectedGridElement:
    | { type: 'NO_SELECTED' }
    | {
        type: 'SELECTED_GRID_ELEMENT';
        gridElement: SelectedGridElement<EvaluatedGridElementType>;
      };
};
export const createCalculationState = (): CalculationState => ({
  initiatingGridElement: derived(initiatingGridElement),
  calculationContext: derived(
    (state: CalculationState): CalculationState['calculationContext'] => {
      const calculationContext =
        state.externallyTriggeredCalculationSetExecution.matches(
          'READY'
        )?.calculationContext;

      return calculationContext
        ? { type: 'AVAILABLE', calculationContext }
        : { type: 'NOT_AVAILABLE' };
    }
  ),
  gridVersionName: derived((state: CalculationState): string | undefined => {
    if (state.calculationContext.type === 'AVAILABLE') {
      return state.calculationContext.calculationContext.gridVersionName;
    }

    return undefined;
  }),
  externallyTriggeredCalculationSetExecution:
    externallyTriggeredCalculationSetExecution.create({ current: 'IDLE' }),
  calculationSets: createGenericFetchMachine<CalculationSets>(),
  functionalGridElements: derived(functionalGridElements),
  calculationSetStatistics: derived(calculationSetStatistics),
  isCalculationTacticResultUnavailable: derived(
    (state: CalculationState): boolean =>
      isCalculationTacticResultUnavailable(state.calculationTactic.value)
  ),
  calculationTacticNotification: { type: 'NO_NOTIFICATION' },
  calculationTactic: createGenericFetchMachine<CalculationTactic>(),
  resultsBrowsingMode: createCalculationTacticResultsBrowsingMachine(),
  gridElementDetails: derived(evaluatedGridElements),
  tacticResultBrowsingFilter: { type: 'NONE_AVAILABLE' },
  previousTacticResultBrowsingFilter: { type: 'NONE_AVAILABLE' },
  evaluationRules: {
    type: "UNAVAILABLE"
  },
  availableCalculationScenarios: derived(availableCalculationScenarios),
  loadFlowSummary: derived(loadFlowSummary),
  loadFlowScenariosRequiringAttention: derived(
    loadFlowScenariosRequiringAttention
  ),
  shortCircuitSummary: derived(shortCircuitSummary),
  shortCircuitCalculationRequiresAttention: derived(
    shortCircuitCalculationRequiresAttention
  ),
  gridElementsDetailsByCalculationType: derived(
    createDetailsFilteredBySelectedScenarios
  ),
  gridElementCalculationSetExecutionDetails: derived(
    createGridElementCalculationSetExecutionDetails
  ),
  gridElementsAttentionSummary: derived(gridElementsAttentionSummary),
  selectedGridElement: derived(selectedGridElement),
  calculationTacticExecutionStatusText: derived(
    calculationTacticExecutionStatusText
  ),
  gridElementsAttentionStatuses: { type: 'NOT_AVAILABLE' }
});

function loadFlowScenariosRequiringAttention(
  state: CalculationState
): CalculationState['loadFlowScenariosRequiringAttention'] {
  if (isCalculationTacticResultUnavailable(state.calculationTactic.value)) {
    return { type: 'NOT_AVAILABLE' };
  }

  const calculation = state.calculationTactic
    .value as CompletedCalculationTactic;
  const scenarios =
    calculation.calculationSetResult.calculationScenarios.loadFlow;
  const scenariosRequiringAttention: Record<string, boolean> = {};
  for (const scenario of scenarios) {
    scenariosRequiringAttention[scenario.scenarioId] =
      doesCalculationScenarioRequiredAttention(state, scenario.scenarioId);
  }

  return { type: 'AVAILABLE', scenarios: scenariosRequiringAttention };
}

const doesCalculationScenarioRequiredAttention = (
  calculation: CalculationState,
  scenarioId: string
) => {
  if(calculation.evaluationRules.type === 'UNAVAILABLE') {
    return false;
  }
  const loadFlowScenarioSummary = getLoadFlowScenarioSummary(
    calculation.calculationTactic
      .value as unknown as NonNullable<CompletedCalculationTactic>,
    calculation.evaluationRules.rules,
    [scenarioId],
    'LowVoltage'
  );
  return loadFlowScenarioSummary.value.attentionRequired;
};
function shortCircuitCalculationRequiresAttention(
  state: CalculationState
):
  | { type: 'NOT_AVAILABLE' }
  | { type: 'AVAILABLE'; requiresAttention: boolean } {
  if (state.gridElementsAttentionStatuses.type === 'NOT_AVAILABLE')
    return { type: 'NOT_AVAILABLE' };
  const x = countShortCircuitIssuesRequiringAttention(
    state.gridElementsAttentionStatuses.byCalculationType
  );
  if (x > 0) {
    return {
      type: 'AVAILABLE',
      requiresAttention: true
    };
  } else {
    return {
      type: 'NOT_AVAILABLE'
    };
  }
}
